import axiosInstance, { apiUrl } from "./api";
import axios from "axios";
const token = localStorage.getItem("token");

export const fetchProducts = async (sourceFilter = null, limit = 50, page = 1, search = null, signal = null) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }
  try {
    const params = {};
    // Always pass source parameter, convert "All" to "all" (lowercase)
    if (sourceFilter) {
      params.source = sourceFilter.toLowerCase();
    } else {
      params.source = "all";
    }
    
    // Add search parameter if provided
    if (search && search.trim()) {
      params.search = search.trim();
    }
    
    // Add pagination parameters for Amazon
    if (sourceFilter && sourceFilter.toLowerCase() === 'amazon') {
      params.limit = limit;
      params.page = page;
    }
    
    const response = await axiosInstance.get("/products", {
      params,
      headers: {
        Authorization: token,
      },
      signal, // Add AbortController signal for request cancellation
    });

    // Handle paginated response for Amazon
    if (sourceFilter && sourceFilter.toLowerCase() === 'amazon' && response.data && response.data.data) {
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error.response || error.message);
    throw error;
  }
};

export const scrapReferenceUrl = async (payload) => {
  try {
    const response = await axiosInstance.post("/upload/website", payload, {
      headers: {
        Authorization: token,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error scraping URL:", error);
    throw error;
  }
};

export const uploadFilesToApi = async (formData) => {
  try {
    const response = await axiosInstance.post("/upload/file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Upload failed:", error);
    throw error;
  }
};

export const uploadCompanyKnowledgeFiles = async (formData) => {
  try {
    const response = await axiosInstance.post("/upload/file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Company knowledge upload failed:", error);
    throw error;
  }
};

// Process a streaming response
export const processStreamResponse = async (
  response,
  onChunk,
  onComplete,
  onError
) => {
  console.warn(
    "Stream processing not supported - use processSSEResponse instead"
  );
  throw new Error("Stream processing not supported in this environment");
};

// Try to parse streaming data
export const tryParseStreamData = (data, isFinal = false) => {
  console.warn("Stream parsing not supported - use processSSEResponse instead");
  return null;
};

export const getColumnsData = (columnCountKey, columns) => {
  const result = {};
  Object.entries(columns).forEach(([colKey, colValue]) => {
    result[colKey] = {
      output: colValue.type?.toLowerCase() || 'text',
      goal: colValue.text.goal || '',
      constraint: colValue.text.constraint || '',
      further_consideration: colValue.text.furtherConsideration || ''
    };
  });
  return result;
};

export const generateProductDescription = async (
  sku,
  keywords,
  section = "",
  furtherDetails = "",
  agentId,
  sessionId,
  previousResponse,
  language,
  locationFocus,
  customPrompt = null,
  customSection = null
) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }
  const modifiedKeywords = keywords?.join(",") || "";
  const payload = {
    request_type: "product_description",
    additional_data: {
      product_sku: sku,
      keywords: modifiedKeywords,
      section_name: section,
      further_details: furtherDetails,
      agent_id: agentId,
      session_id: sessionId,
      streaming: true,
      language: language || "",
      location: locationFocus || "",
    },
  };
  
  // Add previous response if provided
  if (previousResponse) {
    payload.additional_data.previous_response = previousResponse;
  }
  if (customSection) {
    payload.additional_data.custom_section = customSection;
  }
  // Add custom prompt fields if provided
  if (customPrompt) {
    if (customPrompt.reason) {
      payload.additional_data.reason = customPrompt.reason;
    }
    if (customPrompt.role) {
      payload.additional_data.role = customPrompt.role;
    }
    if (customPrompt.goal) {
      payload.additional_data.goal = customPrompt.goal;
    }
    if (customPrompt.constraints) {
      payload.additional_data.constraints = customPrompt.constraints;
    }
  }
  
  try {
    const response = await axiosInstance.post("/generate/product", payload, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      "Error generating product description:",
      error.response || error.message
    );
    throw error;
  }
};

export const getFileIcon = (fileType) => {
  if (fileType.includes("pdf")) {
    return "/pdf.svg";
  } else if (fileType.includes("image")) {
    return "/img.svg";
  } else if (fileType.includes("word")) {
    return "/doc.svg";
  } else if (fileType.includes("csv")) {
    return "/csv.svg";
  }
  return "/doc.svg";
};

export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const generateEmailDescription = async (
  templateName,
  occasion,
  tone,
  furtherDetails,
  agentId,
  sessionId,
  previewImage
) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Unauthorized: No token found");
  const payload = {
    preview_image: previewImage,
    request_type: "email",
    additional_data: {
      agent_id: agentId,
      session_id: sessionId,
      tone: tone,
      occasion: occasion,
      template: templateName,
      further_details: furtherDetails,
    },
  };

  try {
    console.log(
      "Using axios instance with baseURL:",
      axiosInstance.defaults.baseURL
    );
    const response = await axiosInstance.post("/generate/email", payload, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
    });

    if (!response.data) {
      throw new Error("Network response error or no data");
    }

    const data = response.data;
    console.log("Response data:", data);

    try {
      const parsedJson = tryParseSSE(data);
      console.log("Parsed JSON:", parsedJson);

      // If it's an array of sections, return it directly
      if (Array.isArray(parsedJson)) {
        return parsedJson;
      }

      // Return a valid fallback array with one section
      return [
        {
          id: `section-${Date.now()}`,
          component_name: "Banner",
          title: "Welcome!",
          description: "This is a default section.",
          image:
            "https://via.placeholder.com/800x400/e0e0e0/999999?text=Banner+Image",
        },
      ];
    } catch (err) {
      console.error("Parsing failed:", err);
      // Return a valid fallback array with one section
      return [
        {
          id: `section-${Date.now()}`,
          component_name: "Banner",
          title: "Welcome!",
          description: "This is a default section.",
          image:
            "https://via.placeholder.com/800x400/e0e0e0/999999?text=Banner+Image",
        },
      ];
    }
  } catch (error) {
    console.error("Error generating email:", error.response || error.message);
    throw error;
  }
};

const tryParseSSE = (data) => {
  try {
    const lines = data.split("\n");
    let jsonChunks = [];
    for (const line of lines) {
      if (line.startsWith("data:")) {
        const cleaned = line.replace(/^data:\s?/, "");
        if (cleaned !== "") {
          jsonChunks.push(cleaned);
        }
      }
    }

    const combined = jsonChunks.join("");
    const firstBrace = combined.indexOf("{");
    const lastBrace = combined.lastIndexOf("}");

    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const jsonStr = combined.substring(firstBrace, lastBrace + 1);

      try {
        const parsed = JSON.parse(jsonStr);

        if (parsed && parsed.response && Array.isArray(parsed.response)) {
          return parsed.response;
        }
      } catch (err) {
        console.error("❌ Final JSON parsing failed", err);
      }
    }
  } catch (parseErr) {
    console.warn("Error while parsing SSE data:", parseErr);
  }
};

// Process SSE text response
export const processSSEResponse = (text) => {
  try {
    if (!text) return null;

    console.log("Processing SSE text response");

    // Split by lines and process each line
    const lines = text.split("\n");
    let jsonChunks = [];

    for (const line of lines) {
      if (line.startsWith("data:")) {
        const cleaned = line.replace(/^data:\s*/, "").trim();
        if (cleaned && cleaned !== "") {
          jsonChunks.push(cleaned);
        }
      }
    }

    const combined = jsonChunks.join("");
    console.log("Combined JSON chunks:", combined);

    // Find the JSON object boundaries
    const firstBrace = combined.indexOf("{");
    const lastBrace = combined.lastIndexOf("}");

    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const jsonStr = combined.substring(firstBrace, lastBrace + 1);

      try {
        const parsed = JSON.parse(jsonStr);
        console.log("✅ Successfully parsed JSON:", parsed);
        return parsed;
      } catch (err) {
        console.error("❌ JSON parse failed:", err);

        const jsonMatch = combined.match(/(\{.*\}|\[.*\])/s);
        if (jsonMatch) {
          try {
            const parsedBackup = JSON.parse(jsonMatch[0]);
            console.log(
              "✅ Successfully parsed JSON with backup method:",
              parsedBackup
            );
            return parsedBackup;
          } catch (e) {
            console.error("Secondary parsing failed:", e);
          }
        }
      }
    }

    const possibleJsons =
      combined.match(/(\{[^{}]*(((?<!\\\{)\{[^{}]*\})[^{}]*)*\})/g) || [];
    for (const jsonCandidate of possibleJsons) {
      try {
        const lastResortParsed = JSON.parse(jsonCandidate);
        console.log(
          "✅ Successfully parsed JSON with last resort method:",
          lastResortParsed
        );
        return lastResortParsed;
      } catch (e) {
        // Skip and try the next one
      }
    }

    return null;
  } catch (error) {
    console.error("Error processing SSE response:", error);
    return null;
  }
};

export const replacePlaceholders = (html, data) => {
  let updatedHtml = html;

  if (data.header) {
    Object.entries(data.header).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      updatedHtml = updatedHtml.replace(regex, value);
    });
  }

  return updatedHtml;
};

export const generateProductDescriptionStream = async (
  sku,
  keywords,
  section = "",
  furtherDetails = "",
  agentId,
  sessionId,
  onDataChunk,
  onComplete,
  onError,
  previousResponse,
  language,
  location,
  customPrompt = null,
  customSection = null
) => {
  const token = localStorage.getItem("token");
  if (!token) {
    if (onError) onError(new Error("Unauthorized: No token found"));
    return;
  }
  const modifiedKeywords = keywords?.join(",") || "";
  const payload = {
    request_type: "product_description",
    additional_data: {
      product_sku: sku,
      keywords: modifiedKeywords,
      section_name: section,
      further_details: furtherDetails,
      agent_id: agentId,
      session_id: sessionId,
      streaming: true,
      language: language,
      location: location,
    },
  };
  if (previousResponse) {
    payload.additional_data.previous_response = previousResponse;
  }
  if (customSection) {
    payload.additional_data.custom_section = customSection;
  }
  // Add custom prompt fields if provided
  if (customPrompt) {
    if (customPrompt.reason) {
      payload.additional_data.reason = customPrompt.reason;
    }
    if (customPrompt.role) {
      payload.additional_data.role = customPrompt.role;
    }
    if (customPrompt.goal) {
      payload.additional_data.goal = customPrompt.goal;
    }
    if (customPrompt.constraints) {
      payload.additional_data.constraints = customPrompt.constraints;
    }
  }
  let accumulatedData = [];
  try {
    const response = await fetch(`${axiosInstance.defaults.baseURL}/generate/product`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        Accept: "text/event-stream",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorMsg = `HTTP error! status: ${response.status} - ${response.statusText}`;
      if (onError) onError(new Error(errorMsg));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let errorOccurred = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (!errorOccurred && onComplete) await onComplete();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.trim().startsWith("data:")) {
          const jsonStartIndex = line.indexOf('{');
          if (jsonStartIndex > -1) {
            const jsonString = line.substring(jsonStartIndex);
            try {
              const parsed = JSON.parse(jsonString);
              accumulatedData.push(parsed);
              if (onDataChunk) {
                onDataChunk(parsed);
              }
            } catch (err) {
              errorOccurred = true;
              const parseMsg = `Chunk parse error: ${err.message} on line: ${line}`;
              console.warn(parseMsg);
              if (onError) onError(new Error(parseMsg));
              break;
            }
          }
        }
      }
      if (errorOccurred) break;
    }
    return accumulatedData;
  } catch (error) {
    console.error("Streaming error:", error);
    if (onError) onError(new Error(error.message || "Streaming error occurred. Please try again."));
    return accumulatedData;
  }
};

export const productSaveAsDraft = async (productData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const formData = new FormData();
    
    // Add basic product fields
    formData.append("product[title]", productData.title || "");
    formData.append("product[location]", productData.location || "");
    formData.append("product[language]", productData.language || "");
    formData.append("product[product_sku]", productData.product_sku || "");
    formData.append("product[further_details]", productData.further_details || "");
    formData.append("product[status]", productData.status || "draft");
    formData.append("product[keywords]", productData.keywords || "");
    formData.append("product[foray_response]", productData.foray_response || "");
    formData.append("product[product_shopify_id]", productData.product_shopify_id || "");
    formData.append("product[session_id]", productData.session_id || "");
    formData.append("product[agent_id]", productData.agent_id || "");
    // Add images array properly - iterate over each image URL
    if (productData.images && Array.isArray(productData.images)) {
      productData.images.forEach((imageUrl) => {
        if (imageUrl && typeof imageUrl === 'string') {
          formData.append("product[images][]", imageUrl);
        }
      });
    }
    
    // Add hero image
    if (productData.image) {
      formData.append("product[image]", productData.image);
    }

    // Add images if provided
    if (productData.image_upload && Array.isArray(productData.image_upload)) {
      productData.image_upload.forEach((image, index) => {
        if (image instanceof File) {
          formData.append("product[image_uploads][]", image);
          if (productData.image_names && productData.image_names[index]) {
            formData.append("product[image_names][]", productData.image_names[index]);
          }
        }
      });
    }

    const response = await axiosInstance.post("/api/v1/products", formData, {
      headers: {
        Authorization: token,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error creating product:", error.response || error.message);
    throw error;
  }
};

export const deleteProduct = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.delete(`/api/v1/products/${productId}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error deleting product:", error.response || error.message);
    throw error;
  }
};

export const checkProductExists = async (title) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.get(`/api/v1/is_product?title=${encodeURIComponent(title)}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error checking product existence:", error.response || error.message);
    throw error;
  }
};

export const checkProductExistsBySku = async (sku) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.get(`/api/v1/is_product?sku=${encodeURIComponent(sku)}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error checking product existence by SKU:", error.response || error.message);
    throw error;
  }
};

export const updateProduct = async (productId, productData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const formData = new FormData();
    
    // Add basic product fields
    formData.append("product[title]", productData.title || "");
    formData.append("product[location]", productData.location || "");
    formData.append("product[language]", productData.language || "");
    formData.append("product[product_sku]", productData.product_sku || "");
    formData.append("product[further_details]", productData.further_details || "");
    formData.append("product[status]", productData.status || "draft");
    formData.append("product[keywords]", productData.keywords || "");
    
    // Always include foray_response - it contains all product data including metafields
    const forayResponse = productData.foray_response || "";
    if (forayResponse) {
      try {
        const parsed = JSON.parse(forayResponse);
        
      } catch (e) {
        console.log("updateProduct - foray_response is not JSON:", e);
      }
    }
    formData.append("product[foray_response]", forayResponse);
    
    formData.append("product[product_shopify_id]", productData.product_shopify_id || "");
    formData.append("product[session_id]", productData.session_id || "");
    formData.append("product[agent_id]", productData.agent_id || "");

    // Add hero image
    if (productData.image) {
      formData.append("product[image]", productData.image);
    }

    // Add images array properly - iterate over each image URL
    if (productData.images && Array.isArray(productData.images)) {
      productData.images.forEach((imageUrl) => {
        if (imageUrl && typeof imageUrl === 'string') {
          formData.append("product[images][]", imageUrl);
        }
      });
    }

    // Add image uploads if provided
    if (productData.image_upload && Array.isArray(productData.image_upload)) {
      productData.image_upload.forEach((image, index) => {
        if (image instanceof File) {
          formData.append("product[image_uploads][]", image);
          if (productData.image_names && productData.image_names[index]) {
            formData.append("product[image_names][]", productData.image_names[index]);
          }
        }
      });
    }

    const response = await axiosInstance.patch(`/api/v1/products/${productId}`, formData, {
      headers: {
        Authorization: token,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating product:", error.response || error.message);
    throw error;
  }
};

export const updateProductSafely = async (productId, productData, originalProductData = null) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const formData = new FormData();
    
    // Only add title if it's different from the original or if we don't have original data
    if (productData.title && (!originalProductData || originalProductData.title !== productData.title)) {
      formData.append("product[title]", productData.title);
    }
    
    // Add other fields (these shouldn't cause conflicts)
    formData.append("product[location]", productData.location || "");
    formData.append("product[language]", productData.language || "");
    formData.append("product[product_sku]", productData.product_sku || "");
    formData.append("product[further_details]", productData.further_details || "");
    formData.append("product[status]", productData.status || "draft");
    formData.append("product[keywords]", productData.keywords || "");
    
    // Always include foray_response - it contains all product data including metafields
    const forayResponse = productData.foray_response || "";
    console.log("updateProductSafely - foray_response length:", forayResponse.length);
    if (forayResponse) {
      try {
        const parsed = JSON.parse(forayResponse);
        console.log("updateProductSafely - metafields in foray_response:", parsed.metafields?.length || 0);
      } catch (e) {
        console.log("updateProductSafely - foray_response is not JSON:", e);
      }
    }
    formData.append("product[foray_response]", forayResponse);
    
    formData.append("product[product_shopify_id]", productData.product_shopify_id || "");
    formData.append("product[session_id]", productData.session_id || "");
    formData.append("product[agent_id]", productData.agent_id || "");

    // Add hero image
    if (productData.image) {
      formData.append("product[image]", productData.image);
    }

    // Add images array properly - iterate over each image URL
    if (productData.images && Array.isArray(productData.images)) {
      productData.images.forEach((imageUrl) => {
        if (imageUrl && typeof imageUrl === 'string') {
          formData.append("product[images][]", imageUrl);
        }
      });
    }

    // Add image uploads if provided
    if (productData.image_upload && Array.isArray(productData.image_upload)) {
      productData.image_upload.forEach((image, index) => {
        if (image instanceof File) {
          formData.append("product[image_uploads][]", image);
          if (productData.image_names && productData.image_names[index]) {
            formData.append("product[image_names][]", productData.image_names[index]);
          }
        }
      });
    }

    const response = await axiosInstance.patch(`/api/v1/products/${productId}`, formData, {
      headers: {
        Authorization: token,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating product safely:", error.response || error.message);
    throw error;
  }
};

export const updateProductMinimal = async (productId, productData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const formData = new FormData();
    
    // Only include essential fields, skip title entirely
    formData.append("product[location]", productData.location || "");
    formData.append("product[language]", productData.language || "");
    formData.append("product[product_sku]", productData.product_sku || "");
    formData.append("product[further_details]", productData.further_details || "");
    formData.append("product[status]", productData.status || "draft");
    formData.append("product[keywords]", productData.keywords || "");
    
    // Always include foray_response - it contains all product data including metafields
    const forayResponse = productData.foray_response || "";
    console.log("updateProductMinimal - foray_response length:", forayResponse.length);
    if (forayResponse) {
      try {
        const parsed = JSON.parse(forayResponse);
        console.log("updateProductMinimal - metafields in foray_response:", parsed.metafields?.length || 0);
      } catch (e) {
        console.log("updateProductMinimal - foray_response is not JSON:", e);
      }
    }
    formData.append("product[foray_response]", forayResponse);
    
    formData.append("product[product_shopify_id]", productData.product_shopify_id || "");
    formData.append("product[session_id]", productData.session_id || "");
    formData.append("product[agent_id]", productData.agent_id || "");

    // Add hero image
    if (productData.image) {
      formData.append("product[image]", productData.image);
    }

    // Add images array properly - iterate over each image URL
    if (productData.images && Array.isArray(productData.images)) {
      productData.images.forEach((imageUrl) => {
        if (imageUrl && typeof imageUrl === 'string') {
          formData.append("product[images][]", imageUrl);
        }
      });
    }

    // Add image uploads if provided
    if (productData.image_upload && Array.isArray(productData.image_upload)) {
      productData.image_upload.forEach((image, index) => {
        if (image instanceof File) {
          formData.append("product[image_uploads][]", image);
          if (productData.image_names && productData.image_names[index]) {
            formData.append("product[image_names][]", productData.image_names[index]);
          }
        }
      });
    }

    const response = await axiosInstance.patch(`/api/v1/products/${productId}`, formData, {
      headers: {
        Authorization: token,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating product minimal:", error.response || error.message);
    throw error;
  }
};

export const findExistingProduct = async (sku, title) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    // First try to get all products and find the matching one
    const response = await axiosInstance.get("/api/v1/products", {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    if (response.data && response.data.data) {
      const products = response.data.data;
      
      // Try to find by SKU first
      if (sku) {
        const productBySku = products.find(product => 
          product.product_sku === sku || 
          product.sku === sku ||
          product.attributes?.product_sku === sku
        );
        if (productBySku) {
          return { exists: true, product_id: productBySku.id, product: productBySku };
        }
      }
      
      // Then try to find by title
      if (title) {
        const productByTitle = products.find(product => 
          product.title === title ||
          product.attributes?.title === title
        );
        if (productByTitle) {
          return { exists: true, product_id: productByTitle.id, product: productByTitle };
        }
      }
    }
    
    return { exists: false, product_id: null, product: null };
  } catch (error) {
    console.error("Error finding existing product:", error.response || error.message);
    throw error;
  }
};

// Fetch product data by SKU for page initialization
export const fetchProductBySku = async (sku) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!sku) {
    throw new Error("SKU is required");
  }

  try {
    const response = await axiosInstance.get(`/api/v1/products/fetch_by_sku?sku=${encodeURIComponent(sku)}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching product by SKU:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Fetch product data by database ID
export const fetchProductById = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    const response = await axiosInstance.get(`/api/v1/products/${productId}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching product by ID:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Fetch product by ID for public preview (without authentication)
// Uses the same API endpoint as edit page: /api/v1/products/{id}
export const fetchProductByIdPublic = async (productId) => {
  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    // Use axios directly without the instance to avoid automatic token injection
    // This allows public access to preview URLs without requiring login
    const response = await axios.get(`${apiUrl}/api/v1/products/${productId}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching product by ID (public):", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Fetch Shopify product data by ID using the Shopify API
export const fetchShopifyProductById = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    const response = await axiosInstance.get(`/products/${productId}`, {
      headers: {
        Authorization: token,
      },
    });

    return { data: response.data };
  } catch (error) {
    console.error("Error fetching Shopify product by ID:", error);
    if (error.response?.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Fetch Shopify product from the new API endpoint
export const fetchShopifyProductFromNewApi = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    const response = await axiosInstance.get(`/products/${productId}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching Shopify product from new API:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Hide a section for a product
export const hideSection = async (hiddenSectionData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.post("/api/v1/hidden_sections", {
      hidden_section: hiddenSectionData,
    }, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error hiding section:", error.response || error.message);
    throw error;
  }
};

// Unhide a section for a product
export const unhideSection = async (hiddenSectionId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.patch(`/api/v1/hidden_sections/${hiddenSectionId}`, {
      hidden_section: {
        is_hidden: false
      }
    }, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error unhiding section:", error.response || error.message);
    throw error;
  }
};

// Fetch hidden sections 
export const fetchHiddenSections = async (sectionableType, sectionableId, userId = null) => {
  // Use user_id only for authentication (for shared/incognito links)
  if (!userId) {
    throw new Error("Unauthorized: user_id is required");
  }

  try {
    // Only pass sectionable_type, not sectionable_id, to get all hidden sections globally
    const params = {
      sectionable_type: sectionableType,
    };
    
    // Only add sectionable_id if it's not "all" and is provided
    if (sectionableId && sectionableId !== "all") {
      params.sectionable_id = sectionableId;
    }
    
    // Add user_id for authentication
    params.user_id = userId;

    const headers = {
      "Content-Type": "application/json",
    };

    const response = await axiosInstance.get("/api/v1/hidden_sections", {
      params,
      headers,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching hidden sections:", error.response || error.message);
    throw error;
  }
};

// Fetch Amazon product by SKU from the products endpoint
export const fetchAmazonProductBySku = async (sku) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!sku) {
    throw new Error("SKU is required");
  }

  try {
    // Fetch all Amazon products and find the one with matching SKU
    const response = await axiosInstance.get("/products", {
      params: { source: "amazon" },
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    if (response.data && Array.isArray(response.data)) {
      // Find product by SKU (Amazon products use SKU as ID)
      const product = response.data.find(
        (p) => p.id === sku || p.product_sku === sku || p.sku === sku
      );
      
      if (product) {
        return { data: product };
      }
    }
    
    return null; // Product not found
  } catch (error) {
    console.error("Error fetching Amazon product by SKU:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Fetch Amazon product by ID using the comprehensive product endpoint (matches Shopify structure)
export const fetchAmazonProductById = async (productId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!productId) {
    throw new Error("Product ID is required");
  }

  try {
    // Use the same endpoint structure as Shopify: /products/:id?source=amazon
    const response = await axiosInstance.get(`/products/${productId}`, {
      params: { source: "amazon" },
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    // Match Shopify response structure: { success: true, data: {...} }
    if (response.data && response.data.success && response.data.data) {
      return { data: response.data.data };
    }
    
    // Fallback: if response is direct product data
    if (response.data && response.data.id) {
      return { data: response.data };
    }
    
    return null; // Product not found
  } catch (error) {
    console.error("Error fetching Amazon product by ID:", error.response || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Product not found
    }
    throw error;
  }
};

// Force refresh products - useful after creating/updating products
export const refreshProducts = async (sourceFilter = null, limit = 50, page = 1, search = null, signal = null) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }
  
  try {
    // Add a cache-busting parameter to force fresh data
    const timestamp = new Date().getTime();
    const params = { _t: timestamp };
    
    // Always pass source parameter, convert "All" to "all" (lowercase)
    if (sourceFilter) {
      params.source = sourceFilter.toLowerCase();
    } else {
      params.source = "all";
    }
    
    // Add search parameter if provided
    if (search && search.trim()) {
      params.search = search.trim();
    }
    
    // Add pagination parameters for Amazon
    if (sourceFilter && sourceFilter.toLowerCase() === 'amazon') {
      params.limit = limit;
      params.page = page;
    }
    
    const response = await axiosInstance.get("/products", {
      params,
      headers: {
        Authorization: token,
        "Cache-Control": "no-cache",
      },
      signal, // Add AbortController signal for request cancellation
    });

    // Handle paginated response for Amazon
    if (sourceFilter && sourceFilter.toLowerCase() === 'amazon' && response.data && response.data.data) {
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error refreshing products:", error.response || error.message);
    throw error;
  }
};

// Sequence Template API Functions

// Create a new sequence template
export const createSequenceTemplate = async (templateData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.post("/api/v1/sequences", templateData, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error creating sequence template:", error.response || error.message);
    throw error;
  }
};

// Get list of sequence templates
export const getSequenceTemplates = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  try {
    const response = await axiosInstance.get("/api/v1/sequences", {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching sequence templates:", error.response || error.message);
    throw error;
  }
};

// Get sequence template details by ID
export const getSequenceTemplateById = async (templateId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!templateId) {
    throw new Error("Template ID is required");
  }

  try {
    const response = await axiosInstance.get(`/api/v1/sequences/${templateId}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching sequence template:", error.response || error.message);
    throw error;
  }
};

// Update an existing sequence template
export const updateSequenceTemplate = async (templateId, templateData) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!templateId) {
    throw new Error("Template ID is required");
  }

  try {
    const response = await axiosInstance.patch(`/api/v1/sequences/${templateId}`, templateData, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error updating sequence template:", error.response || error.message);
    throw error;
  }
};

// Delete a sequence template
export const deleteSequenceTemplate = async (templateId) => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Unauthorized: No token found");
  }

  if (!templateId) {
    throw new Error("Template ID is required");
  }

  try {
    const response = await axiosInstance.delete(`/api/v1/sequences/${templateId}`, {
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error deleting sequence template:", error.response || error.message);
    throw error;
  }
};